import {
  Fonts,
  FooterType,
  LayoutType,
  LayoutDirection,
  NavStyle,
  MenuStyle,
  ThemeMode,
  ThemeStyle,
  ThemeStyleRadius,
} from './AppEnums';

export const appThemeFont = {
  family: `${['Vinila Variable'].join(',')}!important`,
  weight: {
    light: Fonts.LIGHT,
    regular: Fonts.REGULAR,
    medium: Fonts.MEDIUM,
    semiBold: Fonts.BOLD,
    bold: Fonts.BOLD,
  },
  size: {
    base: '14px',
    lg: '16px',
    md: '14px',
    sm: '12px',
    title: '26px',
  },
};

export interface SidebarData {
  sidebarBgColor: string;
  sidebarTextColor: string;
  sidebarHeaderColor: string;
  sidebarMenuSelectedBgColor: string;
  sidebarMenuSelectedTextColor: string;
  mode: string;
}

export const DarkSidebar = {
  sidebarBgColor: '#313541',
  sidebarTextColor: '#fff',
  sidebarHeaderColor: '#313541',
  sidebarMenuSelectedBgColor: '#F4F7FE',
  sidebarMenuSelectedTextColor: 'rgba(0, 0, 0, 0.87)',
  mode: ThemeMode.DARK,
};
export const LightSidebar = {
  sidebarBgColor: '#fff',
  sidebarTextColor: 'rgba(0, 0, 0, 0.60)',
  sidebarHeaderColor: '#fff',
  sidebarMenuSelectedBgColor: '#F4F7FE',
  sidebarMenuSelectedTextColor: 'rgba(0, 0, 0, 0.87)',
  mode: ThemeMode.LIGHT,
};
export const textLight = {
  primary: 'rgba(35, 40, 46, 1)',
  secondary: '#00795B',
  disabled: 'rgb(149, 156, 169)',
  hint: 'rgb(174, 175, 184)',
  sidebar: 'rgba(62, 69, 77, 1)',
};

export const textDark = {
  primary: 'rgb(255,255,255)',
  secondary: 'rgb(229, 231, 235)',
  disabled: 'rgb(156, 163, 175)',
  hint: 'rgb(174, 175, 184)',
  sidebar: 'rgba(62, 69, 77, 1)',
};

export const backgroundDark = {
  paper: '#2B3137',
  default: '#1F2527',
};

export const backgroundLight = {
  paper: 'rgba(255, 255, 255, 1)',
  default: 'rgba(249, 250, 251, 1)',
};

const cardRadius = ThemeStyleRadius.STANDARD;
declare module 'styled-components' {
  export interface DefaultTheme {
    spacing: number;
    cardRadius: string;
    cardRadius30: string;
    cardShadow: string;
    direction: string;
    palette: {
      mode: string;
      borderColor: string;
      dividerColor: string;
      tooltipBg: string;
      background: typeof backgroundLight;
      brand: string;
      primary: {
        main: string;
        contrastText: string;
        [key: string]: string;
      };
      secondary: {
        main: string;
      };
      success: {
        main: string;
        light: string;
      };
      warning: {
        main: string;
        light: string;
      };
      gray: Record<string, string>;
      black: string;
      white: string;
      orange: Record<string, string>;
      cyan: Record<string, string>;
      red: Record<string, string>;
      green: Record<string, string>;
      blue: Record<string, string>;
      text: typeof textLight;
      neutral: Record<string, string>;
      yellow: Record<string, string>;
      violet: Record<string, string>;
    };
    status: {
      danger: string;
    };
    divider: string;
    font: typeof appThemeFont;
    sidebar: {
      light: typeof LightSidebar;
      dark: typeof DarkSidebar;
    };
    button: {
      disabled: {
        background: string;
        color: string;
        border: string;
      };
    };
    breakpoints: Record<string, number>;
    sizes: {
      line: {
        base: number;
      };
      borderRadius: {
        base: string;
        circle: string;
      };
      framed: {
        base: string;
      };
    };
  }
}
export interface DefaultThemeType {
  spacing: number;
  cardRadius: string;
  cardRadius30: string;
  cardShadow: string;
  direction: string;
  palette: {
    type: string;
    mode: string;
    borderColor: string;
    dividerColor: string;
    tooltipBg: string;
    background: typeof backgroundLight;
    brand: string;
    primary: {
      main: string;
      contrastText: string;
      [key: string]: string;
    };
    secondary: {
      main: string;
    };
    success: {
      main: string;
      light: string;
    };
    warning: {
      main: string;
      light: string;
    };
    gray: Record<string, string>;
    black: string;
    white: string;
    orange: Record<string, string>;
    cyan: Record<string, string>;
    red: Record<string, string>;
    green: Record<string, string>;
    blue: Record<string, string>;
    text: typeof textLight;
    divider: string;
    neutral: Record<string, string>;
    yellow: Record<string, string>;
    violet: Record<string, string>;
  };
  status: {
    danger: string;
  };
  divider: string;
  font: typeof appThemeFont;
  sidebar: {
    light: typeof LightSidebar;
    dark: typeof DarkSidebar;
  };
  button: {
    disabled: {
      background: string;
      color: string;
      border: string;
    };
  };
  breakpoints: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  };
  sizes: {
    line: {
      base: number;
    };
    borderRadius: {
      base: string;
      circle: string;
    };
    framed: {
      base: string;
    };
  };
}

export const defaultTheme = {
  theme: {
    spacing: 4,
    cardRadius: cardRadius,
    cardRadius30: ThemeStyleRadius.MODERN,
    cardShadow: '0 0 5px 5px rgba(0,0,0,0.03)',
    direction: LayoutDirection.LTR, //ltr, rtl
    palette: {
      type: 'light',
      mode: ThemeMode.LIGHT,
      borderColor: '#0000001f',
      dividerColor: 'rgba(173, 147, 147, 0.06)',
      tooltipBg: 'rgba(0, 0, 0, 0.75)',
      background: backgroundLight,
      brand: '#00795B',
      primary: {
        main: '#00795B',
        contrastText: '#fff',
        50: '#f3fffc',
        75: '#CCF4EA',
        100: '#99EAD6',
        200: '#66DFC1',
        300: '#33D5AD',
        400: '#00CA98',
        500: '#00A27A',
        600: '#00795B',
        700: '#00513D',
        800: '#00281E',
      },
      secondary: {
        main: textLight.secondary,
      },
      success: {
        main: '#52c41a', //'#11C15B',
        light: '#D9F5E5',
        500: '#16B364',
        100: '#D3F8DF',
        300: '#73E2A3',
        50: '#EAFEF1',
      },
      warning: {
        main: '#FF5252',
        light: '#FFECDC',
        50: '#FFFAEB',
        100: '#FFF7E0',
        300: '#FFE785',
        700: '#A16607',
      },
      gray: {
        50: '#fafafa',
        100: '#F5F5F5', //#F5F6FA,
        200: '#eeeeee', //#edf2f7',
        300: '#E0E0E0',
        400: '#bdbdbd', //#c5c6cb,
        500: '#9e9e9e', //#A8A8A8',
        600: '#757575', //#666666',
        700: '#616161', //'#4a5568',
        800: '#201e21',
        900: '#212121', //'#1a202c',
        A100: '#d5d5d5',
        A200: '#aaaaaa',
        A400: '#303030',
        A700: '#616161',
      },
      black: '#000',
      white: '#FFFFFF',
      orange: {
        5: '#ffa940',
        6: '#fa8c16',
        7: '#FFF7E0',
        8: '#FFFAEB',
        50: '#fff7ed',
        300: '#ffb86a',
        500: '#ff6900',
        700: '#A16607',
      },
      cyan: {
        7: '#08979c',
        100: '#DDFAFE',
        300: '#67E3F9',
      },
      red: {
        6: '#f5222d',
        7: '#FEE4E2',
        8: '#FFF5F5',
        500: '#FF4D4F',
        ERROR50: '#FFF5F5',
        ERROR430: '#FDA29B',
        ERROR500: '#F04438',
      },
      green: {
        3: '#b7eb8f',
        5: '#73d13d',
        6: '#52c41a',
        7: '#389e0d',
        8: '#D3F8DF',
        9: '#EAFEF1',
        500: '#16B364',
      },
      blue: {
        5: '#40a9ff',
        7: '#096dd9',
        50: '#F0F4FF',
        100: '#D1E0FF',
        300: '#84ADFF',
        500: '#2970FF',
      },
      text: textLight,
      divider: 'rgba(234, 236, 239, 1)',
      neutral: {
        50: '#F9FAFB',
        100: '#EAECEF',
        200: '#D5D9DE',
        300: '#8F9BA6',
        400: '#5C656E',
        500: '#3E454D',
        700: '#23282E',
        900: '#16171A',
      },
      yellow: {
        500: '#D9A407',
      },
      violet: {
        50: '#F5F5FF',
        100: '#EBE9FE',
        300: '#BDB4FE',
        500: '#7A5AF8',
      },
      error: {
        50: '#FFF5F5',
        300: '#FDA29B',
        500: '#F04438',
      },
    },
    status: {
      danger: 'orange',
    },
    divider: 'rgba(234, 236, 239, 1)',
    font: appThemeFont,
    sidebar: {
      light: LightSidebar,
      dark: DarkSidebar,
    },
    button: {
      disabled: {
        background: 'rgba(0, 0, 0, 0.04)',
        color: '',
        border: '',
      },
    },
    breakpoints: {
      xs: 480,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1600,
    },
    sizes: {
      line: {
        base: 1.35,
      },
      borderRadius: {
        base: '8px',
        circle: '50%',
      },
      framed: {
        base: '20px',
      },
    },
  },
};
const defaultConfig = {
  sidebar: {
    borderColor: '#757575',
    menuStyle: MenuStyle.DEFAULT as string,
    allowSidebarBgImage: false,
    sidebarBgImageId: 1,
    colorSet: LightSidebar,
  },

  locale: {
    languageId: 'english',
    locale: 'en',
    name: 'English',
    icon: 'us',
  },
  themeStyle: ThemeStyle.STANDARD,
  direction: LayoutDirection.LTR,
  themeMode: ThemeMode.LIGHT,
  footerType: FooterType.FLUID,
  navStyle: NavStyle.STANDARD,
  layoutType: LayoutType.FULL_WIDTH,
  footer: false,
  iframe: false,
  rtlLocale: ['ar'],
};
export default defaultConfig;
